import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, Search, Shield, ShieldOff, Ban, RefreshCw, 
  Download, ChevronDown, ChevronUp, Loader2, AlertTriangle,
  Check, X, Clock, Mail, Calendar, FileText, UserX, UserCheck, CreditCard,
  Trash2, KeyRound, Copy
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserStatusBadge = ({ status }) => {
  const config = {
    ACTIVE: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', label: 'Active' },
    DEACTIVATED: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30', label: 'Deactivated' },
    BANNED: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', label: 'Banned' },
  };
  const c = config[status] || config.ACTIVE;
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${c.bg} ${c.text} border ${c.border}`}>
      {c.label}
    </span>
  );
};

const BanReasonBadge = ({ code }) => {
  const labels = {
    fraud: 'Fraud',
    abuse: 'Abuse',
    manipulation: 'Manipulation',
    chargeback: 'Chargeback',
    terms_violation: 'Terms Violation',
    other: 'Other'
  };
  return <span className="text-xs text-gray-400">{labels[code] || code}</span>;
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const limit = 20;

  // Modal states
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showUnbanModal, setShowUnbanModal] = useState(false);
  const [showCancelSubModal, setShowCancelSubModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState('');
  const [showTempPwModal, setShowTempPwModal] = useState(false);
  const [customTempPw, setCustomTempPw] = useState('');
  const [issuedTempPw, setIssuedTempPw] = useState(null);
  const [copiedTempPw, setCopiedTempPw] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Form states
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [banReasonCode, setBanReasonCode] = useState('other');
  const [cancelImmediate, setCancelImmediate] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status_filter', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', limit);
      params.append('skip', skip);

      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/users?${params}`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setTotal(data.total || 0);
      } else {
        console.error('Failed to fetch users:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
    setLoading(false);
  }, [statusFilter, searchQuery, skip]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    setReason('');
    setNotes('');
    setTicketId('');
    setBanReasonCode('other');
    setCancelImmediate(false);
  };

  const handleDeactivate = async () => {
    if (!selectedUser || !reason) return;
    setActionLoading(true);
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id || selectedUser.user_id}/deactivate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ reason, notes, ticket_id: ticketId || null })
      });
      
      if (response.ok) {
        setShowDeactivateModal(false);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to deactivate user');
      }
    } catch (error) {
      alert('Failed to deactivate user');
    }
    setActionLoading(false);
  };

  const handleReactivate = async () => {
    if (!selectedUser || !reason) return;
    setActionLoading(true);
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id || selectedUser.user_id}/reactivate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ reason, notes, ticket_id: ticketId || null })
      });
      
      if (response.ok) {
        setShowReactivateModal(false);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to reactivate user');
      }
    } catch (error) {
      alert('Failed to reactivate user');
    }
    setActionLoading(false);
  };

  const handleBan = async () => {
    if (!selectedUser || !reason) return;
    setActionLoading(true);
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id || selectedUser.user_id}/ban`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ 
          reason, 
          reason_code: banReasonCode,
          notes, 
          ticket_id: ticketId || null 
        })
      });
      
      if (response.ok) {
        setShowBanModal(false);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to ban user');
      }
    } catch (error) {
      alert('Failed to ban user');
    }
    setActionLoading(false);
  };

  const handleUnban = async () => {
    if (!selectedUser || !reason) return;
    setActionLoading(true);
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id || selectedUser.user_id}/unban`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ reason, notes, ticket_id: ticketId || null })
      });
      
      if (response.ok) {
        setShowUnbanModal(false);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to unban user');
      }
    } catch (error) {
      alert('Failed to unban user');
    }
    setActionLoading(false);
  };

  const handleCancelSubscription = async () => {
    if (!selectedUser || !reason) return;
    setActionLoading(true);
    
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/users/${selectedUser.id || selectedUser.user_id}/cancel-subscription`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ 
          reason, 
          immediate: cancelImmediate,
          notes, 
          ticket_id: ticketId || null 
        })
      });
      
      if (response.ok) {
        setShowCancelSubModal(false);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to cancel subscription');
      }
    } catch (error) {
      alert('Failed to cancel subscription');
    }
    setActionLoading(false);
  };

  const downloadReport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status_filter', statusFilter);
      
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/admin/users/reports/export?${params}`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_report_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download report:', error);
    }
  };

  const openActionModal = (user, action) => {
    setSelectedUser(user);
    resetForm();
    
    switch (action) {
      case 'deactivate':
        setShowDeactivateModal(true);
        break;
      case 'reactivate':
        setShowReactivateModal(true);
        break;
      case 'ban':
        setShowBanModal(true);
        break;
      case 'unban':
        setShowUnbanModal(true);
        break;
      case 'cancel-subscription':
        setShowCancelSubModal(true);
        break;
      case 'delete':
        setDeleteConfirmEmail('');
        setShowDeleteModal(true);
        break;
      case 'temp-password':
        setCustomTempPw('');
        setIssuedTempPw(null);
        setCopiedTempPw(false);
        setShowTempPwModal(true);
        break;
      default:
        break;
    }
  };

  const handleAssignTempPassword = async () => {
    if (!selectedUser || !reason) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(
        `${API_URL}/api/admin/users/${selectedUser.id || selectedUser.user_id}/temp-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            reason,
            notes: notes || null,
            ticket_id: ticketId || null,
            custom_password: customTempPw || null,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setIssuedTempPw(data.temporary_password);
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to issue temporary password');
      }
    } catch (error) {
      alert('Failed to issue temporary password');
    }
    setActionLoading(false);
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || !reason || !deleteConfirmEmail) return;
    setActionLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(
        `${API_URL}/api/admin/users/${selectedUser.id || selectedUser.user_id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            reason,
            confirm_email: deleteConfirmEmail,
            notes: notes || null,
            ticket_id: ticketId || null,
          }),
        }
      );

      if (response.ok) {
        setShowDeleteModal(false);
        resetForm();
        setDeleteConfirmEmail('');
        fetchUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      alert('Failed to delete user');
    }
    setActionLoading(false);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-[#3B82F6]" />
          <h2 className="text-lg font-semibold text-white">User Management</h2>
          <span className="text-sm text-gray-400">({total} users)</span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            onClick={downloadReport}
            variant="outline"
            size="sm"
            className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
          >
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
          <Button
            onClick={fetchUsers}
            variant="outline"
            size="sm"
            className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Search by email or username..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSkip(0); }}
            className="pl-10 bg-[#1e2a45] border-[#2d3a56] text-white"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setSkip(0); }}
          className="px-3 py-2 bg-[#1e2a45] border border-[#2d3a56] rounded-lg text-gray-300 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="DEACTIVATED">Deactivated</option>
          <option value="BANNED">Banned</option>
        </select>
      </div>

      {/* Users Table */}
      <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 text-[#3B82F6] animate-spin" />
            <span className="ml-2 text-gray-400">Loading users...</span>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No users found
          </div>
        ) : (
          <div className="divide-y divide-[#1e2a45]">
            {users.map((user) => (
              <div key={user.id || user.user_id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#3B82F6]/20 flex items-center justify-center">
                      <span className="text-[#3B82F6] font-semibold">
                        {(user.email || 'U')[0].toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">{user.email}</span>
                        <UserStatusBadge status={user.status || 'ACTIVE'} />
                        {user.is_subscribed && (
                          <span className="px-2 py-0.5 text-xs bg-[#F97316]/20 text-[#F97316] rounded-full border border-[#F97316]/30">
                            Subscribed
                          </span>
                        )}
                        {user.is_admin && (
                          <span className="px-2 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded-full border border-purple-500/30">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400 flex items-center gap-3 mt-0.5">
                        {user.username && <span>@{user.username}</span>}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(user.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Cancel Subscription button - show for subscribed users */}
                    {user.is_subscribed && !user.is_admin && (
                      <Button
                        onClick={() => openActionModal(user, 'cancel-subscription')}
                        variant="outline"
                        size="sm"
                        className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                      >
                        <CreditCard className="h-4 w-4 mr-1" />
                        Cancel Sub
                      </Button>
                    )}
                    
                    {/* Action buttons based on status */}
                    {(user.status || 'ACTIVE') === 'ACTIVE' && !user.is_admin && (
                      <>
                        <Button
                          onClick={() => openActionModal(user, 'deactivate')}
                          variant="outline"
                          size="sm"
                          className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                        >
                          <ShieldOff className="h-4 w-4 mr-1" />
                          Deactivate
                        </Button>
                        <Button
                          onClick={() => openActionModal(user, 'ban')}
                          variant="outline"
                          size="sm"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <Ban className="h-4 w-4 mr-1" />
                          Ban
                        </Button>
                      </>
                    )}

                    {/* Delete is available for any non-admin user (active, deactivated, or banned). */}
                    {!user.is_admin && (
                      <>
                        <Button
                          onClick={() => openActionModal(user, 'temp-password')}
                          variant="outline"
                          size="sm"
                          data-testid={`temp-password-${user.id || user.user_id}`}
                          className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                        >
                          <KeyRound className="h-4 w-4 mr-1" />
                          Temp Password
                        </Button>
                        <Button
                          onClick={() => openActionModal(user, 'delete')}
                          variant="outline"
                          size="sm"
                          data-testid={`delete-user-${user.id || user.user_id}`}
                          className="border-red-700/40 text-red-500 hover:bg-red-700/10"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                    
                    {user.status === 'DEACTIVATED' && (
                      <>
                        <Button
                          onClick={() => openActionModal(user, 'reactivate')}
                          variant="outline"
                          size="sm"
                          className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                        >
                          <UserCheck className="h-4 w-4 mr-1" />
                          Reactivate
                        </Button>
                        <Button
                          onClick={() => openActionModal(user, 'ban')}
                          variant="outline"
                          size="sm"
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          <Ban className="h-4 w-4 mr-1" />
                          Ban
                        </Button>
                      </>
                    )}
                    
                    {user.status === 'BANNED' && (
                      <Button
                        onClick={() => openActionModal(user, 'unban')}
                        variant="outline"
                        size="sm"
                        className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Shield className="h-4 w-4 mr-1" />
                        Unban
                      </Button>
                    )}
                    
                    <button
                      onClick={() => setExpandedUserId(expandedUserId === (user.id || user.user_id) ? null : (user.id || user.user_id))}
                      className="p-2 text-gray-400 hover:text-white"
                    >
                      {expandedUserId === (user.id || user.user_id) ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Expanded details */}
                {expandedUserId === (user.id || user.user_id) && (
                  <div className="mt-4 pt-4 border-t border-[#1e2a45]">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">User ID</span>
                        <p className="text-gray-300 font-mono text-xs mt-1">{user.id || user.user_id}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Auth Provider</span>
                        <p className="text-gray-300 mt-1">{user.auth_provider || 'Google'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Subscription</span>
                        <p className="text-gray-300 mt-1" data-testid={`user-${user.id || user.user_id}-plan`}>
                          {user.is_subscribed
                            ? (user.subscription_plan || user.subscription_tier || 'Active')
                            : 'None'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Subscription Expires</span>
                        <p className="text-gray-300 mt-1" data-testid={`user-${user.id || user.user_id}-expires`}>
                          {user.subscription_expires_at
                            ? new Date(user.subscription_expires_at).toLocaleString()
                            : '—'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Stripe Customer</span>
                        <p
                          className="text-gray-300 mt-1 font-mono text-xs break-all"
                          data-testid={`user-${user.id || user.user_id}-stripe-customer`}
                        >
                          {user.stripe_customer_id || '—'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Stripe Subscription</span>
                        <p
                          className="text-gray-300 mt-1 font-mono text-xs break-all"
                          data-testid={`user-${user.id || user.user_id}-stripe-subscription`}
                        >
                          {user.stripe_subscription_id || '—'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Last Updated</span>
                        <p className="text-gray-300 mt-1">
                          {user.updated_at ? new Date(user.updated_at).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                    
                    {/* Status-specific info */}
                    {user.status === 'DEACTIVATED' && user.deactivated_at && (
                      <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                        <div className="flex items-center gap-2 text-yellow-400 text-sm font-medium mb-2">
                          <ShieldOff className="h-4 w-4" />
                          Deactivated
                        </div>
                        <p className="text-gray-300 text-sm">
                          <span className="text-gray-500">Date:</span> {new Date(user.deactivated_at).toLocaleString()}
                        </p>
                        {user.deactivation_reason && (
                          <p className="text-gray-300 text-sm mt-1">
                            <span className="text-gray-500">Reason:</span> {user.deactivation_reason}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {user.status === 'BANNED' && user.banned_at && (
                      <div className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                        <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-2">
                          <Ban className="h-4 w-4" />
                          Banned
                        </div>
                        <p className="text-gray-300 text-sm">
                          <span className="text-gray-500">Date:</span> {new Date(user.banned_at).toLocaleString()}
                        </p>
                        {user.ban_reason_code && (
                          <p className="text-gray-300 text-sm mt-1">
                            <span className="text-gray-500">Reason Code:</span> <BanReasonBadge code={user.ban_reason_code} />
                          </p>
                        )}
                        {user.ban_reason && (
                          <p className="text-gray-300 text-sm mt-1">
                            <span className="text-gray-500">Details:</span> {user.ban_reason}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {total > limit && (
          <div className="flex items-center justify-between p-4 border-t border-[#1e2a45]">
            <span className="text-sm text-gray-400">
              Showing {skip + 1}-{Math.min(skip + limit, total)} of {total}
            </span>
            <div className="flex gap-2">
              <Button
                onClick={() => setSkip(Math.max(0, skip - limit))}
                disabled={skip === 0}
                variant="outline"
                size="sm"
                className="border-[#1e2a45] text-gray-300"
              >
                Previous
              </Button>
              <Button
                onClick={() => setSkip(skip + limit)}
                disabled={skip + limit >= total}
                variant="outline"
                size="sm"
                className="border-[#1e2a45] text-gray-300"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Deactivate Modal */}
      <Dialog open={showDeactivateModal} onOpenChange={setShowDeactivateModal}>
        <DialogContent className="bg-[#131b2e] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ShieldOff className="h-5 w-5 text-yellow-400" />
              Deactivate User
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20 text-sm text-yellow-300">
              <p>This will:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Block user from logging in</li>
                <li>Cancel subscription at period end (not immediately)</li>
                <li>Preserve all user data</li>
              </ul>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Reason *</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for deactivation..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
                rows={3}
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Notes (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Ticket ID (optional)</label>
              <Input
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                placeholder="Support ticket reference..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={() => setShowDeactivateModal(false)}
                variant="outline"
                className="border-[#1e2a45] text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeactivate}
                disabled={!reason || actionLoading}
                className="bg-yellow-600 hover:bg-yellow-700 text-white"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Deactivate User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reactivate Modal */}
      <Dialog open={showReactivateModal} onOpenChange={setShowReactivateModal}>
        <DialogContent className="bg-[#131b2e] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-green-400" />
              Reactivate User
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/20 text-sm text-green-300">
              <p>This will:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Allow user to log in again</li>
                <li>Force fresh login (old sessions cleared)</li>
                <li>NOT automatically restart billing</li>
              </ul>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Reason *</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for reactivation..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
                rows={3}
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Notes (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Ticket ID (optional)</label>
              <Input
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                placeholder="Support ticket reference..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={() => setShowReactivateModal(false)}
                variant="outline"
                className="border-[#1e2a45] text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReactivate}
                disabled={!reason || actionLoading}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Reactivate User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ban Modal */}
      <Dialog open={showBanModal} onOpenChange={setShowBanModal}>
        <DialogContent className="bg-[#131b2e] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Ban className="h-5 w-5 text-red-400" />
              Ban User
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/20 text-sm text-red-300">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              <strong>Warning:</strong> This is a permanent action.
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Immediately blocks all access</li>
                <li>Immediately cancels subscription (no refund)</li>
                <li>Removes from leaderboards</li>
                <li>Requires admin approval to reverse</li>
              </ul>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Ban Reason Code *</label>
              <select
                value={banReasonCode}
                onChange={(e) => setBanReasonCode(e.target.value)}
                className="w-full px-3 py-2 bg-[#1e2a45] border border-[#2d3a56] rounded-lg text-white"
              >
                <option value="fraud">Fraud</option>
                <option value="abuse">Abuse</option>
                <option value="manipulation">Market Manipulation</option>
                <option value="chargeback">Chargeback</option>
                <option value="terms_violation">Terms Violation</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Detailed Reason *</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter detailed reason for ban..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
                rows={3}
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Notes (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes or evidence reference..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Ticket ID (optional)</label>
              <Input
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                placeholder="Support ticket reference..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={() => setShowBanModal(false)}
                variant="outline"
                className="border-[#1e2a45] text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBan}
                disabled={!reason || actionLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Ban User Permanently
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unban Modal */}
      <Dialog open={showUnbanModal} onOpenChange={setShowUnbanModal}>
        <DialogContent className="bg-[#131b2e] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-400" />
              Unban User
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20 text-sm text-blue-300">
              <p>This will:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>Remove the ban</li>
                <li>Set status to DEACTIVATED (not ACTIVE)</li>
                <li>Require separate reactivation to allow login</li>
              </ul>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Reason *</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for removing ban..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
                rows={3}
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Notes (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Ticket ID (optional)</label>
              <Input
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                placeholder="Support ticket reference..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={() => setShowUnbanModal(false)}
                variant="outline"
                className="border-[#1e2a45] text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUnban}
                disabled={!reason || actionLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                Remove Ban
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelSubModal} onOpenChange={setShowCancelSubModal}>
        <DialogContent className="bg-[#131b2e] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-purple-400" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20 text-sm text-purple-300">
              <p>Cancel subscription options:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li><strong>At period end:</strong> User keeps access until current period ends</li>
                <li><strong>Immediately:</strong> Access revoked right away (use for abuse cases)</li>
              </ul>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-[#1e2a45]/50 rounded-lg">
              <input
                type="checkbox"
                id="cancelImmediate"
                checked={cancelImmediate}
                onChange={(e) => setCancelImmediate(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-[#1e2a45] text-red-500 focus:ring-red-500"
              />
              <label htmlFor="cancelImmediate" className="text-sm text-gray-300">
                Cancel immediately (revoke access now)
              </label>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Reason *</label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason for canceling subscription..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
                rows={3}
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Notes (optional)</label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Ticket ID (optional)</label>
              <Input
                value={ticketId}
                onChange={(e) => setTicketId(e.target.value)}
                placeholder="Support ticket reference..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <Button
                onClick={() => setShowCancelSubModal(false)}
                variant="outline"
                className="border-[#1e2a45] text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCancelSubscription}
                disabled={!reason || actionLoading}
                className={cancelImmediate 
                  ? "bg-red-600 hover:bg-red-700 text-white" 
                  : "bg-purple-600 hover:bg-purple-700 text-white"
                }
              >
                {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                {cancelImmediate ? 'Cancel Immediately' : 'Cancel at Period End'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* DELETE USER (PERMANENT) MODAL */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md bg-[#050810] border-red-700/40">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Permanently Delete User
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This action cannot be undone. The user account, sessions, push tokens, and pending idea submissions will be removed. Trades and market positions are preserved.
            </DialogDescription>
          </DialogHeader>

          {selectedUser && (
            <div className="space-y-4 mt-2">
              <div className="bg-red-700/10 border border-red-700/30 rounded-lg p-3">
                <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Deleting</div>
                <div className="text-white font-mono text-sm break-all">{selectedUser.email}</div>
                {selectedUser.username && (
                  <div className="text-xs text-gray-500 mt-1">@{selectedUser.username}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Reason <span className="text-red-400">*</span>
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for permanent deletion (audit trail)"
                  className="bg-[#1e2a45] border-[#2d3a56] text-white"
                  rows={2}
                  data-testid="delete-user-reason"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Type the user's email to confirm <span className="text-red-400">*</span>
                </label>
                <Input
                  value={deleteConfirmEmail}
                  onChange={(e) => setDeleteConfirmEmail(e.target.value)}
                  placeholder={selectedUser.email}
                  className="bg-[#1e2a45] border-[#2d3a56] text-white font-mono"
                  data-testid="delete-user-confirm-email"
                />
                {deleteConfirmEmail && deleteConfirmEmail.toLowerCase() !== (selectedUser.email || '').toLowerCase() && (
                  <p className="text-xs text-red-400 mt-1">Email does not match</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Notes (optional)
                </label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal notes"
                  className="bg-[#1e2a45] border-[#2d3a56] text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Ticket ID (optional)
                </label>
                <Input
                  value={ticketId}
                  onChange={(e) => setTicketId(e.target.value)}
                  placeholder="Support ticket reference"
                  className="bg-[#1e2a45] border-[#2d3a56] text-white"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <Button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 bg-[#1e2a45] hover:bg-[#2d3a56] text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteUser}
                  disabled={
                    actionLoading ||
                    !reason ||
                    deleteConfirmEmail.toLowerCase() !== (selectedUser.email || '').toLowerCase()
                  }
                  data-testid="confirm-delete-user-btn"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Permanently Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* TEMP PASSWORD MODAL */}
      <Dialog open={showTempPwModal} onOpenChange={(open) => {
        setShowTempPwModal(open);
        if (!open) { setIssuedTempPw(null); setCustomTempPw(''); setCopiedTempPw(false); }
      }}>
        <DialogContent className="sm:max-w-md bg-[#050810] border-amber-500/40">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-amber-400" />
              {issuedTempPw ? 'Temporary Password Issued' : 'Issue Temporary Password'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {issuedTempPw
                ? 'Copy this password and send it to the user out-of-band. It will not be shown again.'
                : 'Forces a password reset on the user\'s next login. Existing sessions are revoked.'}
            </DialogDescription>
          </DialogHeader>

          {selectedUser && !issuedTempPw && (
            <div className="space-y-4 mt-2">
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">For user</div>
                <div className="text-white font-mono text-sm break-all">{selectedUser.email}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Reason <span className="text-red-400">*</span>
                </label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is the temp password being issued? (audit trail)"
                  className="bg-[#1e2a45] border-[#2d3a56] text-white"
                  rows={2}
                  data-testid="temp-pw-reason"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Custom password (optional)
                </label>
                <Input
                  value={customTempPw}
                  onChange={(e) => setCustomTempPw(e.target.value)}
                  placeholder="Leave blank to auto-generate a 16-char password"
                  className="bg-[#1e2a45] border-[#2d3a56] text-white font-mono"
                  data-testid="temp-pw-custom"
                />
                <p className="text-xs text-gray-500 mt-1">8–64 chars. Leave empty for a strong random password.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Notes (optional)
                </label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Internal notes"
                  className="bg-[#1e2a45] border-[#2d3a56] text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Ticket ID (optional)
                </label>
                <Input
                  value={ticketId}
                  onChange={(e) => setTicketId(e.target.value)}
                  placeholder="Support ticket reference"
                  className="bg-[#1e2a45] border-[#2d3a56] text-white"
                />
              </div>

              <div className="flex gap-3 pt-1">
                <Button
                  onClick={() => setShowTempPwModal(false)}
                  className="flex-1 bg-[#1e2a45] hover:bg-[#2d3a56] text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignTempPassword}
                  disabled={actionLoading || !reason || (customTempPw && (customTempPw.length < 8 || customTempPw.length > 64))}
                  data-testid="confirm-temp-pw-btn"
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />}
                  Issue Password
                </Button>
              </div>
            </div>
          )}

          {issuedTempPw && (
            <div className="space-y-4 mt-2">
              <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg p-4">
                <div className="text-xs text-amber-300 uppercase tracking-wide mb-2 font-semibold">
                  Temporary password (one-time view)
                </div>
                <div className="bg-[#050810] border border-amber-500/30 rounded p-3 font-mono text-amber-100 text-sm break-all" data-testid="issued-temp-pw">
                  {issuedTempPw}
                </div>
                <Button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(issuedTempPw);
                      setCopiedTempPw(true);
                      setTimeout(() => setCopiedTempPw(false), 2000);
                    } catch { /* ignore */ }
                  }}
                  variant="outline"
                  size="sm"
                  className="mt-3 border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                >
                  {copiedTempPw ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copiedTempPw ? 'Copied' : 'Copy to clipboard'}
                </Button>
              </div>
              <p className="text-xs text-gray-400">
                The user is now flagged <span className="text-amber-300 font-mono">must_reset_password=true</span> and all their existing sessions have been revoked. They&apos;ll be forced to set a new password on their next login.
              </p>
              <Button
                onClick={() => setShowTempPwModal(false)}
                className="w-full bg-[#1e2a45] hover:bg-[#2d3a56] text-white"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
